import React, { memo, useCallback, useEffect, useState } from "react";
import { useTranslate } from "helpers";
import { Button } from "ui/components/Button/Button";
import { ContactEditFormValues, ContactNoteRow } from "services/api/contacts/types";
import { getContactNotesAPI, deleteContactNoteAPI } from "services/api/contacts/api";
import { initialDataWithPagination } from "services/api/consts";
import { Loader } from "ui/components/Loader";
import { TableSearch } from "ui/components/Table/TableSearch";
import { CONTACT_EDIT_NOTE_COLUMNS } from "./consts";
import ContactNoteEditCard from "ui/modules/contacts/pages/ContactDetailPage/components/ContactDetailCard/components/ContactNoteEditCard/ContactNoteEditCard";

interface ContactNoteStepProps {
  formValues: ContactEditFormValues;
}

const ContactNoteStep: React.FC<ContactNoteStepProps> = ({ formValues: parentFormValues }) => {
  const contactId = parentFormValues.id;
  const { t } = useTranslate();
  const [notes, setNotes] = useState<ContactNoteRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editNoteId, setEditNoteId] = useState<string | null>(null);

  const fetchNotes = useCallback(async () => {
    if (!contactId) return;
    setIsLoading(true);
    try {
      const result = await getContactNotesAPI(initialDataWithPagination.pagination, contactId);
      setNotes(result.data);
    } catch {
      setNotes([]);
    }
    setIsLoading(false);
  }, [contactId]);

  useEffect(() => {
    if (contactId) fetchNotes();
  }, [contactId]);

  const handleRowClick = useCallback((noteId: string) => {
    setEditNoteId(noteId);
    setShowEdit(true);
  }, []);

  const handleAddNew = () => {
    setEditNoteId(null);
    setShowEdit(true);
  };

  // Klik na kantu ne smije otvoriti i uređivanje retka.
  const handleDeleteClick = async (event: React.MouseEvent<HTMLDivElement>, noteId: string) => {
    event.stopPropagation();
    event.preventDefault();
    if (!contactId) return;
    await deleteContactNoteAPI(t, contactId, noteId);
    fetchNotes();
  };

  const handleEditSuccess = () => {
    setShowEdit(false);
    setEditNoteId(null);
    fetchNotes();
  };

  return (
    <>
      <div className="flex items-center justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
        <div className="w-full max-w-4xl">
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">Bilješke {parentFormValues.contactTypeId === "C" ? "tvrtke" : "osobe"}</h1>
          <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
            Dodajte bilješke o kontaktu klikom na gumb <strong>"Dodaj bilješku"</strong>.
            Nakon unosa možete nastaviti na sljedeći korak.
          </p>

          {isLoading ? (
            <Loader isLoading={true}> </Loader>
          ) : (
            <div className="px-3">
              {notes.length === 0 ? (
                <div className="max-w-md mx-auto">
                  <Button
                    variant="primary"
                    size="full"
                    startIcon="/media/icons/plus.svg"
                    children="Dodaj bilješku"
                    onClick={handleAddNew}
                  />
                </div>
              ) : (
                <>
                  {/* Isti izgled kao PP aparati na unosu vozila: klik na redak uređuje, kanta briše. */}
                  <TableSearch
                    columns={CONTACT_EDIT_NOTE_COLUMNS(handleDeleteClick)}
                    data={notes}
                    selectableRows={false}
                    onRowClick={handleRowClick}
                    isLoading={isLoading}
                  />
                  <div className="flex justify-end mt-3">
                    <Button variant="primary" startIcon="/media/icons/plus.svg" children="Dodaj bilješku" onClick={handleAddNew} />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <ContactNoteEditCard
        contactId={contactId ?? ""}
        noteId={editNoteId}
        show={showEdit}
        onSuccess={handleEditSuccess}
        onClose={() => setShowEdit(false)}
        
      />
    </>
  );
};

export default memo(ContactNoteStep);
